import { styled, Typography, useTheme } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { useIsSyncedStore } from '~/redux/hooks/store'
import { useSelectedProfile, useUserRole } from '~/redux/hooks/user'
import { PROFILE_TYPES } from '~/modules/auth/constants'
import { urlWithStoreID } from '~/utils/common'
import { ROLES } from '~/constants/settings'
import { Icon } from 'bs-unified-ui'
import PropTypes from 'prop-types'

const StyledContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  background: theme.background.sectionBg5,
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 100,
  '& button': {
    marginTop: theme.spacing(2),
  },
}))

const StyledDialog = styled('div')(({ theme }) => ({
  background: 'white',
  display: 'flex',
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  flexDirection: 'column',
  justifyContent: 'center',
  textAlign: 'center',
  maxWidth: 280,
  alignItems: 'center',
  '& span': {
    lineHeight: '24px',
    fontSize: 14,
    fontFamily: 'Lato',
  },
  '& button': {
    marginTop: theme.spacing(2),
  },
}))

const StyledLink = styled('div')(({ theme }) => ({
  color: theme.accent.blue,
  fontSize: 14,
  marginTop: theme.spacing(1),
  fontFamily: 'Lato',
  cursor: 'pointer',
  '& > span': {
    marginLeft: theme.spacing(0.5),
  },
}))

const StoreSyncingWarning = ({
  children,
  page,
}: { children: React.ReactNode; page: string }) => {
  const theme = useTheme()
  const profileType = useSelectedProfile()
  const { storeId } = useParams()
  const isSyncedStore = useIsSyncedStore()
  const navigate = useNavigate()
  const navigateStoreDetail = () => {
    if (profileType === PROFILE_TYPES.ECOMMERCE) {
      navigate(urlWithStoreID('/settings/ecommerce', storeId))
    } else if (profileType === PROFILE_TYPES.MULTI_STORE) {
      navigate(urlWithStoreID('/admin-options/stores', storeId))
    } else if (profileType === PROFILE_TYPES.CANNABIS) {
      navigate(urlWithStoreID('/settings/pos', storeId))
    }
  }

  const userRole = useUserRole()

  return (
    <>
      {children}
      {!isSyncedStore ? (
        <StyledContainer>
          <StyledDialog>
            <Typography variant="fs14">{`${page ? `${page} data` : 'Data'} will be available once eCommerce data is synced.`}</Typography>
            {[ROLES.OWNER, ROLES.ADMIN].includes(userRole) ? (
              <StyledLink onClick={navigateStoreDetail} role="presentation">
                <Icon
                  name="external-link"
                  size={16}
                  color={theme.accent.blue}
                />
                <span>Sync now</span>
              </StyledLink>
            ) : (
              ''
            )}
          </StyledDialog>
        </StyledContainer>
      ) : (
        ''
      )}
    </>
  )
}

StoreSyncingWarning.defaultProps = {
  children: '',
  page: '',
}

StoreSyncingWarning.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
    PropTypes.any,
  ]),
  page: PropTypes.string,
}

export default StoreSyncingWarning
